import { uniqBy } from 'lodash';
import moment from 'moment';

import { AbsenceStatus } from '~/data/absences';
export * from './constants';

export type EmployeeCalendarDate = {
  id: string;
  date: Date;
  day: string;
  weekday: string;
  inRange: boolean;
  isWeekend: boolean;
  isToday: boolean;
};

export type EmployeeCalendarMonth = {
  id: string;
  label: string;
  showOnDate: Date;
  daysCount: number;
};

export type EmployeeCalendarPerson = {
  employeeId: number;
  firstName: string;
  lastName: string;
  profilePic?: string;
};

export type EmployeeCalendarPersonWithLegalEntityId = EmployeeCalendarPerson & {
  legalEntityId: number;
};

export type EmployeeCalendarEvent = EmployeeCalendarPerson & {
  uId: string;
  startDate: Date;
  endDate?: Date;
  eventCategory: string;
  eventCode: string;
  status: string;
};

export type EmployeeCalendarEventColors = {
  color: string;
  backgroundColor: string;
};

export type EmployeeCalendarEventLegend = {
  key: string;
  content: string;
  colors?: EmployeeCalendarEventColors;
  label: string;
  visibilityToggle?: {
    isVisible: boolean;
    isEnabled: boolean;
  };
};

export type EmployeeCalendarLegendKeys = string[];
export type EmployeeCalendarLegendRows = EmployeeCalendarLegendKeys[];

export type EmployeeCalendarLegendConfig = {
  legendRows: EmployeeCalendarLegendRows;
};

export const isEventInCalendarRange = (props: {
  event: { startDate: Date; endDate?: Date } | EmployeeCalendarEvent;
  calendar: { startDate: Date; endDate: Date };
}) => {
  const { event, calendar } = props;
  if (!!event.endDate) {
    const eventStartsAfterRange = moment(event.startDate).isAfter(
      calendar.endDate,
      'day',
    );
    const eventEndsBeforeRange = moment(event.endDate).isBefore(
      calendar.startDate,
      'day',
    );

    return !(eventStartsAfterRange || eventEndsBeforeRange);
  }

  return moment(event.startDate).isSameOrBefore(
    moment(calendar.endDate),
    'day',
  );
};

export const processEvents = (events: EmployeeCalendarEvent[]) => {
  return events
    .filter(({ status }) => status !== AbsenceStatus.REJECTED)
    .map((event): EmployeeCalendarEvent => {
      const { startDate, endDate } = event;

      return {
        ...event,
        startDate: moment(startDate).startOf('day').toDate(),
        endDate: !!endDate
          ? moment(endDate).startOf('day').toDate()
          : undefined,
      };
    });
};

export const isToday = (date: Date) => moment().isSame(date, 'date');

export const isWeekendDay = (date: Date) => {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 6 || dayOfWeek === 0;
};

export const isOutOfRange = (date: Date, startDate: Date, endDate: Date) => {
  const values = {
    date: moment(date).utc().startOf('day'),
    startDate: moment(startDate).utc().startOf('day'),
    endDate: moment(endDate).utc().startOf('day'),
  };

  return (
    values.date.isBefore(values.startDate) ||
    values.date.isAfter(values.endDate)
  );
};

export const resolveCalendarRangeCapDate = (date: Date) => {
  // Reset Date values time & timezone to avoid comparison issues
  return moment(moment(date).format('YYYY-MM-DD')).toDate();
};

export const handleCalendarRangeEndDST = (date: Date): Date => {
  // Hacky way to handle date comparison mismatch due to Daylight Saving Time
  const endDate = moment(date);
  return endDate.isDST() ? date : endDate.add(6, 'hours').toDate();
};

export const getEmployeeCalendarPersonsFromEvents = (
  events: EmployeeCalendarEvent[],
): EmployeeCalendarPerson[] => {
  return uniqBy(events, (event) => event.employeeId).map(
    (event): EmployeeCalendarPerson => {
      const { employeeId, firstName, lastName, profilePic } = event;

      return {
        employeeId,
        firstName,
        lastName,
        profilePic,
      };
    },
  );
};
